class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {(() => void) | null} */
  #carouselCleanup = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#initPagedCarousels();
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#carouselCleanup?.();
    this.#carouselCleanup = null;
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      this.#initPagedCarousels();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#initPagedCarousels();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Bind all product carousels inside this section.
   * Desktop shows N cards (PC: 4); arrows move one card at a time.
   */
  #initPagedCarousels() {
    this.#carouselCleanup?.();

    /** @type {(() => void)[]} */
    const cleanups = [];

    this.querySelectorAll('[data-recommend-paged-carousel]').forEach((root) => {
      const cleanup = this.#bindPagedCarousel(/** @type {HTMLElement} */ (root));
      if (cleanup) cleanups.push(cleanup);
    });

    this.#carouselCleanup = () => {
      cleanups.forEach((fn) => fn());
    };
  }

  /**
   * @param {HTMLElement} root
   * @returns {(() => void) | null}
   */
  #bindPagedCarousel(root) {
    const track = /** @type {HTMLElement | null} */ (root.querySelector('[data-recommend-track]'));
    const prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-recommend-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-recommend-next]'));
    if (!track || !prev || !next) return null;

    const desktopMq = window.matchMedia('(min-width: 750px)');
    const slides = () => Array.from(track.children).filter((el) => el.nodeType === 1);
    const getPageSize = () => Number(root.dataset.pageSize || 4);
    const isDesktop = () => desktopMq.matches;

    let index = 0;

    const maxIndex = () => Math.max(0, slides().length - getPageSize());

    const slideStep = () => {
      const first = /** @type {HTMLElement | undefined} */ (slides()[0]);
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const updateArrows = () => {
      if (!isDesktop()) {
        prev.hidden = true;
        next.hidden = true;
        prev.disabled = true;
        next.disabled = true;
        return;
      }

      const atStart = index <= 0;
      const atEnd = index >= maxIndex();
      prev.hidden = atStart;
      next.hidden = atEnd;
      prev.disabled = atStart;
      next.disabled = atEnd;
    };

    const goToIndex = (nextIndex) => {
      if (!isDesktop()) {
        track.style.transform = '';
        index = 0;
        updateArrows();
        return;
      }

      index = Math.max(0, Math.min(nextIndex, maxIndex()));
      const offset = slideStep() * index;
      track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      updateArrows();
    };

    const onPrev = (event) => {
      event.preventDefault();
      if (!isDesktop()) return;
      goToIndex(index - 1);
    };

    const onNext = (event) => {
      event.preventDefault();
      if (!isDesktop()) return;
      goToIndex(index + 1);
    };

    const onResize = () => {
      if (!isDesktop()) {
        goToIndex(0);
        return;
      }
      index = Math.min(index, maxIndex());
      goToIndex(index);
    };

    prev.addEventListener('click', onPrev);
    next.addEventListener('click', onNext);
    window.addEventListener('resize', onResize);

    goToIndex(0);

    return () => {
      prev.removeEventListener('click', onPrev);
      next.removeEventListener('click', onNext);
      window.removeEventListener('resize', onResize);
    };
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
